<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
    <div class="d-contents">
        <v-layout justify-end
                  class="mb-8 flex-grow-0">
            <v-btn color="secondary"
                   v-if="canAddGroup"
                   @click="openForm(null)"
                   data-test="addNewGroupBtn">{{ $t('buttons.add_new_employee_group') }}</v-btn>
        </v-layout>

        <Pagination :pagination="pagination"
                    data-test="pagination"
                    @change-page="changePage($event)"
                    @change-amount="changeAmount($event)"></Pagination>

        <v-data-table :headers="headers"
                      :loading="loader"
                      disable-sort
                      :items="rawEmployeesGroups"
                      height="100%"
                      fixed-header
                      disable-pagination
                      hide-default-footer
                      @contextmenu:row="showContextMenu"
                      class="elevation-1 table-wrapper">
            <template v-for="(headerItem, i) in headers"
                      v-slot:[`header.${headerItem.value}`]="{ header }">
                <SortableHeader :header-item="header"
                                :key="i"
                                @sort="sort($event)"></SortableHeader>
            </template>

            <template v-slot:item.actions="{ item }">
                <HintingIcon class-name="mr-2"
                             :icon="ICONS.EDIT"
                             v-if="canEditGroup"
                             @click="openForm(item)"
                             :data-test="item"
                             :tooltip="$t('actions.edit')"></HintingIcon>

                <ButtonWithConfirmation :item-id="item.id"
                                        v-if="canRemoveGroup"
                                        @action-executed="emitFetch"
                                        :callback="deleteGroup"></ButtonWithConfirmation>
            </template>
        </v-data-table>

        <v-menu
            v-model="contextMenuVisible"
            :position-x="contextMenuX"
            :position-y="contextMenuY"
            :close-on-content-click="false"
            absolute
            offset-y
        >
            <v-list>
                <v-list-item v-if="canEditGroup" @click="openForm(contextMenuItem)">
                    <v-list-item-title class="d-flex align-center">
                        <v-icon left dense>{{ ICONS.EDIT }}</v-icon>{{ $t('actions.edit') }}
                    </v-list-item-title>
                </v-list-item>
                <v-divider v-if="canRemoveGroup" class="my-2" />
                <v-list-item v-if="canRemoveGroup && !confirmDelete" @click.prevent="confirmDelete = true">
                    <v-list-item-title class="d-flex align-center">
                        <v-icon left dense>{{ ICONS.DELETE }}</v-icon>{{ $t('actions.delete') }}
                    </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="canRemoveGroup && confirmDelete" @click="deleteGroup(contextMenuItem.id)">
                    <v-list-item-title class="d-flex align-center error--text">
                        <v-icon left dense>{{ ICONS.DELETE_CONFIRM }}</v-icon>{{ $t('messages.delete_confirmation') }}
                    </v-list-item-title>
                </v-list-item>
              </v-list>
       </v-menu>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { ICONS } from '@/constants/icons';
import HintingIcon from '@/components/widgets/HintingIcon';
import ButtonWithConfirmation from '@/components/widgets/buttons/ButtonWithConfirmation';
import Pagination from '@/components/widgets/tables/Pagination';
import SortableHeader from '@/components/widgets/tables/SortableHeader';

export default {
    name: 'EmployeesGroupsTable',
    components: { HintingIcon, ButtonWithConfirmation, Pagination, SortableHeader },
    props: {
        loader: {
            type: Boolean,
            'default': false
        },
        changedPage: {
            type: [String, Number],
            required: false
        }
    },
    data: () => ({
        ICONS,
        pagination: {
            page: 1,
            amount: 20,
            amountAll: null
        },
        sortable: {
            name: 'asc'
        },
        contextMenuVisible: false,
        contextMenuItem: null,
        contextMenuX: 0,
        contextMenuY: 0,
        confirmDelete: false
    }),
    computed: {
        ...mapState('employeesGroups', [
            'rawEmployeesGroups',
            'totalEmployeesGroupsAmount'
        ]),
        headers () {
            return [
                {
                    text: this.$t('labels.employee_group_name'),
                    value: 'name',
                    icon: false,
                    sortable: true
                },
                {
                    text: this.$t('incidents.table.headers.actions'),
                    value: 'actions',
                    icon: false,
                    width: '90px',
                    sortable: false
                }
            ];
        },
        canAddGroup () {
            return this.$can(this.$abilityActions.CREATE, this.$abilitySubjects.EMPLOYEE_GROUP);
        },
        canEditGroup () {
            return this.$can(this.$abilityActions.UPDATE, this.$abilitySubjects.EMPLOYEE_GROUP);
        },
        canRemoveGroup () {
            return this.$can(this.$abilityActions.DELETE, this.$abilitySubjects.EMPLOYEE_GROUP);
        }
    },
    watch: {
        totalEmployeesGroupsAmount: {
            handler (val) {
                this.pagination.amountAll = val;
            },
            immediate: true
        },
        changedPage: {
            handler (val) {
                if (val !== this.pagination.page) {
                    this.pagination.page = val;
                }
            }
        },
        contextMenuVisible: {
            handler (val) {
                if (!val) {
                    this.contextMenuItem = null;
                    this.confirmDelete = false;
                }
            }
        }
    },
    methods: {
        changePage (event) {
            this.pagination.page = event;
            this.$emit('change-page', event);
        },
        changeAmount (event) {
            this.pagination.page = 1;
            this.pagination.amount = event;
            this.$emit('change-amount', event);
        },
        sort (event) {
            switch (this.sortable[event]) {
            case null:
                this.sortable[event] = 'desc';
                break;
            case 'asc':
                this.sortable[event] = 'desc';
                break;
            case 'desc':
                this.sortable[event] = 'asc';
                break;
            }

            this.$emit('sort', {
                name: event,
                value: this.sortable[event]
            });
        },
        deleteGroup (id) {
            return this.$store.dispatch('manageEmployeesGroup/deleteEmployeesGroup', id);
        },
        openForm (item = null) {
            if (item) {
                this.$store.commit('manageEmployeesGroup/SET_GROUP_DATA', item);
                this.$store.commit('manageEmployeesGroup/SET_EDITED_GROUP_ID', item.id);
                this.$store.commit('manageEmployeesGroup/SET_GROUP_FORM_VISIBILITY', true);
            } else {
                this.$store.commit('manageEmployeesGroup/SET_GROUP_FORM_VISIBILITY', true);
            }
            this.contextMenuVisible = false;
        },
        emitFetch () {
            this.$emit('fetch');
        },
        showContextMenu (event, { item }) {
            event.preventDefault();
            this.contextMenuVisible = false;
            this.contextMenuX = event.clientX;
            this.contextMenuY = event.clientY;
            this.$nextTick(() => {
                this.contextMenuVisible = true;
                this.contextMenuItem = item.id;
            });
        }
    }
};
</script>

<style scoped>
.table-wrapper {
    overflow: hidden;
}
</style>
